import React, { cloneElement, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Toolbar,
  useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PropTypes from "prop-types";
import Logo from "../../Logo/homelys_logo.png";
import { useLocation } from "react-router-dom";
import { Sidebar } from "../SideBar/Sidebar";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: (props) => {
      if (!props.isHome || props.elevation > 0) return "rgba(246, 244, 239, 0.97)";
      return "transparent";
    },
    color: (props) => (props.isHome && props.elevation === 0 ? "#ffffff" : "#292720"),
    padding: "0 36px",
    [theme.breakpoints.down("sm")]: {
      padding: "0 12px",
    },
    [theme.breakpoints.between("sm", "md")]: {
      padding: "0 16px",
    },
    top: 0,
    zIndex: 999,
    transition: "all 0.25s ease",
    border: 0,
    boxShadow: (props) =>
      !props.isHome || props.elevation > 0 ? "0 4px 24px rgba(30, 28, 23, 0.08)" : "none",
  },
  centeredLogo: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    cursor: "pointer",
    display: "block",
    width: "170px",
    maxWidth: "38vw",
    [theme.breakpoints.down("sm")]: {
      width: "138px",
    },
  },
  menuControl: {
    minWidth: "auto",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 0",
    color: "inherit",
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#a68757",
    },
  },
  menuLabel: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  enquiryButton: {
    marginLeft: "auto",
    padding: "10px 12px",
    borderRadius: 0,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    color: (props) =>
      props.isHome && props.elevation === 0 ? "#ffffff" : "#292720",
    transition: "color 180ms ease, border-color 180ms ease",
    borderBottom: "1px solid transparent",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#d5bd91",
      borderBottomColor: "#a68757",
    },
    "& .MuiButton-startIcon": {
      marginRight: "8px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "8px 0 8px 8px",
      fontSize: "0.64rem",
      letterSpacing: "0.06em",
    },
  },
}));

function ElevationScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });
  return cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
}

ElevationScroll.propTypes = {
  children: PropTypes.element.isRequired,
  window: PropTypes.func,
};

const Navbar = (props) => {
  const location = useLocation();
  const [elevation, setElevation] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const history = useHistory();
  const isHome = location.pathname === "/";
  const classes = useStyles({ elevation, isHome });

  const pages = [
    { name: "Home", path: "/" },
    { name: "Construction", path: "/construction" },
    { name: "Real-Estate", path: "/real-estate" },
    { name: "About Us", path: "/about-us" },
    { name: "Careers", path: "/careers" },
  ];

  const handleScroll = () => {
    setElevation(window.scrollY > 0 ? 4 : 0);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Close drawer when location changes
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <ElevationScroll elevation={elevation} {...props}>
        <AppBar position={isHome ? "fixed" : "sticky"} className={classes.appBar}>
          <Toolbar disableGutters sx={{ position: "relative", minHeight: { xs: 60, sm: 68 } }}>
            <Button
              className={classes.menuControl}
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
              onClick={drawerOpen ? handleDrawerClose : handleDrawerOpen}
              disableRipple
            >
              {drawerOpen ? <CloseIcon /> : <MenuIcon />}
              <Box component="span" className={classes.menuLabel}>
                Menu
              </Box>
            </Button>
            <Box
              component="img"
              src={Logo}
              alt="Homely Buildofiy"
              onClick={() => history.push("/")}
              className={classes.centeredLogo}
            />
            <Button
              variant="text"
              className={classes.enquiryButton}
              startIcon={<EditNoteOutlinedIcon />}
              onClick={() => props.setIsFormOpen(true)}
            >
              Enquire
            </Button>
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <Sidebar
        drawerOpen={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        pages={pages}
      />
    </React.Fragment>
  );
};

export default Navbar;